import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiGet, apiPost } from "../../shared/apiClient";
import { useToast } from "../../shared/ToastContext";
import {
  ProjectEditorFormFields,
  defaultProjectForm
} from "./ProjectEditorFormFields";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function NewProjectPage() {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [form, setForm] = useState({
    ...defaultProjectForm,
    customerId: isAdmin ? "" : String(user?.customerId ?? "")
  });

  useEffect(() => {
    if (!isAdmin) return;
    void (async () => {
      const res = await apiGet("/api/Customers");
      if (res.ok) setCustomers(Array.isArray(res.data) ? res.data : []);
    })();
  }, [isAdmin]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const customerId = isAdmin
      ? Number(form.customerId || 0)
      : Number(user?.customerId || 0);
    if (!customerId) {
      toast.push("Customer is required", "error");
      return;
    }
    if (!String(form.name || "").trim()) {
      toast.push("Name is required", "error");
      return;
    }

    const payload = {
      customerId,
      code: String(form.code || "").trim() || null,
      name: String(form.name || "").trim(),
      description: String(form.description || "").trim() || null,
      location: String(form.location || "").trim() || null,
      content: form.content || null,
      detailContentMode: form.detailContentMode === "json" ? "json" : "html",
      sectionsJson: String(form.sectionsJson || "").trim() || null,
      image: String(form.image || "").trim() || null,
      order: toNumber(form.order, 0),
      inactive: !!form.inactive
    };

    setSaving(true);
    const res = await apiPost("/api/Projects", payload);
    setSaving(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to create project"), "error");
      return;
    }
    toast.push("Project created", "success");
    navigate("/projects");
  };

  return (
    <div className="w-full space-y-4">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Add project</h1>
            <p className="mt-1 text-sm text-slate-500">Create a project with optional landing page content.</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigate("/projects")}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Back
            </button>
            <button
              type="submit"
              form="new-project-form"
              disabled={saving}
              className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Create project"}
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <form id="new-project-form" onSubmit={onSubmit}>
          <ProjectEditorFormFields
            form={form}
            setForm={setForm}
            isAdmin={isAdmin}
            customers={customers}
            htmlMode={htmlMode}
            setHtmlMode={setHtmlMode}
          />
        </form>
      </div>
    </div>
  );
}
